import { PostureChip } from '@/components/ui/posture-chip';
import type { Recommendation } from '@/lib/api/health-readiness';
import { cn } from '@/lib/utils/cn';
import { ChartTooltip, useChartTooltip } from './chart-tooltip';
import {
  POSTURE_FILL,
  SrTable,
  VERDICT_LABEL,
  VERDICT_POSTURE,
  cssVars,
  f1,
  formatPct,
  type Thresholds,
} from './shared';
import { useCountUp, useEntryMotion } from './use-entry-motion';

/**
 * ReadinessGauge — the go-live readiness meter (prototype `gauge()`).
 *
 * A semicircle 0..100 with the two gates as tinted zones (No-Go,
 * Conditional, Go) and ticks at the thresholds; the fill is the weighted
 * total. The fill takes the verdict's colour once every criterion is
 * scored (the API sends a verdict), and stays ink-500 while scoring is
 * partial. The centre shows the total and the verdict chip, or "n of m
 * scored". Nothing is computed here: total, verdict and counts are props.
 *
 * Motion: the fill sweeps in from 0 on entry, and from the previous value
 * when the value changes; the number counts with it. None under reduced
 * motion.
 */
export interface ReadinessGaugeProps {
  /** Weighted total 0..100 from the API; null when nothing is scored. */
  value: number | null;
  thresholds: Thresholds;
  /** From the API; null until every criterion is scored. */
  verdict: Recommendation | null;
  scored: number;
  total: number;
  /** The report cover's thinner ring. */
  small?: boolean;
  /** Change it (a tab or row id) to replay the entry animation. */
  motionKey?: string | number;
  className?: string;
}

const CX = 130;
const CY = 128;
const R = 104;

function pt(v: number, rad = R): [number, number] {
  const a = Math.PI * (1 - v / 100);
  return [CX + rad * Math.cos(a), CY - rad * Math.sin(a)];
}

function arc(a: number, b: number, rad = R): string {
  const [x1, y1] = pt(a, rad);
  const [x2, y2] = pt(b, rad);
  return `M${x1.toFixed(2)} ${y1.toFixed(2)} A${rad} ${rad} 0 0 1 ${x2.toFixed(2)} ${y2.toFixed(2)}`;
}

export function ReadinessGauge({
  value,
  thresholds,
  verdict,
  scored,
  total,
  small = false,
  motionKey,
  className,
}: ReadinessGaugeProps) {
  const sw = small ? 16 : 20;
  const entry = useEntryMotion(motionKey);
  const change = useEntryMotion(value);
  const { tip, bind } = useChartTooltip();

  const updating = change.runKey > 0 && change.animate;
  const animate = entry.animate || updating;
  const from = updating ? (change.previousKey ?? 0) : 0;
  const shown = useCountUp(value, from, animate, `${entry.runKey}.${change.runKey}`);

  const c = thresholds.conditional;
  const g = thresholds.go;
  const zones: Array<[number, number, string]> = [
    [0, c - 0.7, 'var(--posture-critical-tint)'],
    [c + 0.7, g - 0.7, 'var(--posture-at-risk-tint)'],
    [g + 0.7, 100, 'var(--posture-healthy-tint)'],
  ];
  const fill = verdict ? POSTURE_FILL[VERDICT_POSTURE[verdict]] : 'var(--ink-500)';
  const tick = (v: number) => {
    const [x1, y1] = pt(v, R - sw / 2 - 3);
    const [x2, y2] = pt(v, R + sw / 2 + 3);
    const [lx, ly] = pt(v, R + sw / 2 + 14);
    return (
      <g key={`t${v}`}>
        <line
          x1={f1(x1)}
          y1={f1(y1)}
          x2={f1(x2)}
          y2={f1(y2)}
          stroke="var(--ink-700)"
          strokeWidth={1.5}
        />
        <text x={f1(lx)} y={f1(ly + 3)} textAnchor="middle">
          {v}
        </text>
      </g>
    );
  };

  const name =
    value === null
      ? 'Go-live readiness not scored'
      : `Go-live readiness ${Math.round(value)} percent`;
  const tableRows: Array<[string, string]> = [
    ['Weighted total', value === null ? 'Not scored' : formatPct(value)],
    ['Verdict', verdict ? VERDICT_LABEL[verdict] : 'Waits for every criterion'],
    ['Scored', `${scored} of ${total}`],
    ['Go at', `${g}%`],
    ['Conditional at', `${c}%`],
  ];

  return (
    <div
      key={entry.runKey}
      className={cn('relative mx-auto w-full max-w-[260px]', animate && 'chart-anim', className)}
      {...(value === null
        ? {}
        : bind({
            title: 'Go-live readiness',
            value: formatPct(value),
            sub: `Weighted total · Go at ${g}%, Conditional at ${c}%`,
          }))}
    >
      <svg
        viewBox="0 -18 260 170"
        role="img"
        aria-label={name}
        className="chart-svg chart-svg-sm block h-auto w-full overflow-visible"
      >
        {zones.map(([a, b, col]) => (
          <path key={col} d={arc(a, b)} stroke={col} strokeWidth={sw} fill="none" />
        ))}
        {value === null ? null : (
          <path
            key={change.runKey}
            className="a-sweep"
            d={arc(0, 100)}
            pathLength={100}
            stroke={fill}
            strokeWidth={sw}
            fill="none"
            strokeDasharray={`${value} 100`}
            style={cssVars({ '--from': from })}
          />
        )}
        {tick(c)}
        {tick(g)}
        <text x={CX - R} y={CY + 16} textAnchor="middle">
          0
        </text>
        <text x={CX + R} y={CY + 16} textAnchor="middle">
          100
        </text>
      </svg>
      <div aria-hidden className="absolute inset-x-0 bottom-6 flex flex-col items-center gap-1">
        {value === null ? (
          <div className="text-[40px] font-semibold leading-[44px] tracking-[-0.02em] text-ink-300">
            —
          </div>
        ) : (
          <div className="text-[40px] font-semibold leading-[44px] tracking-[-0.02em] text-ink-900">
            {shown ?? Math.round(value)}%
          </div>
        )}
        {verdict && value !== null ? (
          <PostureChip
            status={VERDICT_POSTURE[verdict]}
            dot
            aria-label={`Verdict: ${VERDICT_LABEL[verdict]}`}
          >
            {VERDICT_LABEL[verdict]}
          </PostureChip>
        ) : (
          <PostureChip status="unassessed" aria-label={`${scored} of ${total} scored`}>
            {scored} of {total} scored
          </PostureChip>
        )}
      </div>
      <SrTable caption="Go-live readiness" head={['Measure', 'Value']} rows={tableRows} />
      <ChartTooltip tip={tip} />
    </div>
  );
}
